import type { Entity } from '@backstage/catalog-model';
import { isTechRadarEntity } from '@ns/plugin-ns-tech-radar-common';
import type { PageHeaderDescriptionItem } from '@ns/plugin-ns-theme';

/**
 * Build the description-list rows shown by `<PageHeader>` (via the
 * `descriptions` prop) for a TechRadar entity.
 *
 * Returns rows for Scope and Owner unconditionally, and Contact only when
 * `entity.spec.contact` is present. Owner and Contact are rendered as
 * `<EntityRefLink>` (via `refTarget`), so Backstage resolves the entity-ref
 * to a humanised display name automatically.
 */
export function buildTechRadarDescriptions(
  entity: Entity,
): PageHeaderDescriptionItem[] {
  if (!isTechRadarEntity(entity)) {
    return [];
  }

  const { contact, owner, scope } = entity.spec;
  const rows: PageHeaderDescriptionItem[] = [
    {
      label: 'Scope',
      value: scope && scope.length > 0 ? scope.join(', ') : '—',
    },
  ];

  if (owner) {
    rows.push({ label: 'Owner', refTarget: owner, value: owner });
  }

  if (contact) {
    rows.push({ label: 'Contact', refTarget: contact, value: contact });
  }

  return rows;
}

/**
 * Return true when the entity has a TechDocs reference annotation, so the
 * `Documentation` tab should be rendered.
 */
export function hasTechDocs(entity: Entity): boolean {
  return Boolean(entity.metadata.annotations?.['backstage.io/techdocs-ref']);
}
